import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import { Box } from "@mui/material";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import SystemButton from "../../components/UIToolkit/SystemButton/SystemButton";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { ConsecutiveMasterSchedule } from "./consecutive/ConsecutiveMasterSchedule";
import { LONG_PTO_BUTTON_INSET_PX } from "./LongPTOButton/consts";
import { LongPTOButton } from "./LongPTOButton/LongPTOButton";
import { ScheduleTemplatePageTab, ScheduleTemplateTabs, useScheduleTemplateTab } from "./ScheduleTemplateTabs";
import { MasterScheduleV2 } from "./v2/MasterScheduleV2";
import { MasterScheduleCoverage } from "./v3/MasterScheduleCoverage";

/**
 * Entry point for the Schedule Template route. Renders the weekly (MasterScheduleV2), consecutive
 * (4/2), or coverage layout based on the persisted, flag-gated tab selection, and injects the shared
 * Weekly/Consecutive/Coverage tab switcher into the active view's header via its `trailing` slot. The
 * People axis has moved to the Staffing area (see `StaffingPeopleView`), so this wrapper no longer
 * mounts it.
 */
export const ScheduleTemplatePageWrapper = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { chosenDepartment } = useHospitalData();
	const { selectedTab, setSelectedTab, isConsecutiveEnabled, isCoverageEnabled } = useScheduleTemplateTab();

	// The layout tabs come first and the Locations link sits last: the tabs switch the view you are
	// already in, while Locations leaves for another page — so it reads as the trailing escape hatch
	// rather than a fourth peer of Weekly/Consecutive/Coverage.
	const tabs = (
		<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
			<ScheduleTemplateTabs
				selectedTab={selectedTab}
				onChange={setSelectedTab}
				isConsecutiveEnabled={isConsecutiveEnabled}
				isCoverageEnabled={isCoverageEnabled}
			/>
			<SystemButton
				buttonVariant="borderless"
				size="small"
				leadingIcon={<PlaceOutlinedIcon fontSize="small" />}
				onClick={() => navigate(`/${chosenDepartment.department_id}/locations`)}
				tooltip={t("locations.entry.tooltip")}
				data-testid="schedule-template-locations-link"
				sx={{ bgcolor: "transparent" }}
			>
				{t("locations.entry.label")}
			</SystemButton>
		</Box>
	);

	const view = ((): ReactNode => {
		if (selectedTab === ScheduleTemplatePageTab.Coverage) return <MasterScheduleCoverage trailing={tabs} />;
		if (selectedTab === ScheduleTemplatePageTab.Consecutive) return <ConsecutiveMasterSchedule trailing={tabs} />;
		return <MasterScheduleV2 trailing={tabs} />;
	})();

	return (
		<>
			{view}
			<Box sx={{ position: "fixed", bottom: LONG_PTO_BUTTON_INSET_PX, left: LONG_PTO_BUTTON_INSET_PX, zIndex: 4 }}>
				<LongPTOButton />
			</Box>
		</>
	);
};
